var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * @packageDocumentation
 * @module      CorAccessBar.ts
 * @description Encapsulates the Shui-compliant Access Bar for this application.
 * @copyright   Copyright (c) 2021 by Corindus Inc., a Siemens Healthineers Company. All rights reserved.
 */
import { LitElement, html, customElement, css, internalProperty } from 'lit-element';
import './CorThemeSelector';
import LoggedInUser from "./LoggedInUser";
import NewPasswordQualifier from "./NewPasswordQualifier";
import ValidationTracker from "./ValidationTracker";
import CloudApi from "./CloudApi";
import { UserLoggedOutEvent } from "./UserLoggedOutEvent";
import { universalStyles } from "./CorMain";
/**
 * Encapsulates the Shui-compliant Access Bar for this application.
 * @noInheritDoc
 */
let CorAccessBar = class CorAccessBar extends LitElement {
    constructor() {
        super(...arguments);
        this.validationTracker = new ValidationTracker();
        /** True if the Settings popover should show; false if not. */
        this.showSettings = false;
        this.showChangePasswordModal = false;
        this.oldPassword = '';
        this.newPassword = '';
        this.confirmPassword = '';
        this.popoverTitle = '';
        this.popoverIsError = false;
        this.popoverText = '';
        this.showPopover = false;
        this.showPasswordExpiresSoon = LoggedInUser.passwordExpiresSoon;
    }
    /**
     * LitElement looks for a static property by this name. If it finds one, it applies these styles to the component.
     * See https://lit-element.polymer-project.org/guide/styles#expressions
     */
    static get styles() {
        return css `
          ${universalStyles}

          sh-popover {
            position: relative;
            float: right;
            top: 0;
            right: 20px;
            z-index: 100;
            margin-bottom: -1000px; // Otherwise the nav bar gets pushed down.
          }
                    
          #settings {
            position: absolute;
            top: 70px;
            width: auto;
            height: auto;
          }
          
          #successOrFailure {
            height: 150px;
            width: 330px;
          }

          #passwordExpiresSoon {
            height: 200px;
            width: 300px;
          }

          #changePasswordContents {
            display: flex;
            height: 90%;
            flex-direction: column;
            justify-content: space-evenly;
          }
        `;
    }
    cancelPasswordExpiresSoon() {
        this.showPasswordExpiresSoon = false;
    }
    hidePopover() {
        this.showPopover = false;
    }
    displayPopover(title, isError, text) {
        this.popoverTitle = title;
        this.popoverIsError = isError;
        this.popoverText = text;
        this.showPopover = true;
    }
    isValidNewPassword() {
        var _a;
        return NewPasswordQualifier.qualifies(this.newPassword, (_a = LoggedInUser.userDetails) === null || _a === void 0 ? void 0 : _a.username, this.oldPassword);
    }
    inputHelper(targetProperty) {
        const self = this;
        return function (e) {
            if (e.currentTarget instanceof Element) {
                // @ts-ignore implicit type of any
                self[targetProperty] = e.currentTarget.getAttribute('value') || '';
            }
        };
    }
    isValidConfirmPassword() {
        return this.confirmPassword.length === 0 ||
            this.confirmPassword === this.newPassword;
    }
    showSettingsPopover() {
        this.showSettings = true;
        this.showChangePasswordModal = false;
    }
    hideSettingsPopover() {
        this.showSettings = false;
    }
    doChangePasswordModal() {
        this.showSettings = false;
        this.oldPassword = '';
        this.newPassword = '';
        this.confirmPassword = '';
        this.showChangePasswordModal = true;
        this.showPasswordExpiresSoon = false;
    }
    logOut() {
        LoggedInUser.logOut();
        this.dispatchEvent(new UserLoggedOutEvent());
    }
    cancelChangePassword() {
        this.showChangePasswordModal = false;
    }
    doChangePassword() {
        new CloudApi().changePassword(LoggedInUser.userDetails.username, this.oldPassword, this.newPassword).then(() => {
            this.displayPopover('New Password Saved', false, 'You have successfully changed your password.');
            this.showChangePasswordModal = false;
        }, (error) => {
            this.displayPopover('Error Changing Password', true, '' +
                'There was a problem changing your password. ' + error);
        });
    }
    /** Renders the component as part of LitElement's lifecycle. */
    render() {
        var _a;
        const username = ((_a = LoggedInUser.userDetails) === null || _a === void 0 ? void 0 : _a.username) || '';
        // Recreate the menu from scratch each time. This prevents Shui from remembering which one was
        // highlighted ("active") when the Settings popup was dismissed and highlighting that one again when the popup
        // is made visible again..
        let settingsPopover = this.showSettings ? html `
            <sh-popover id="settings" target="userIdentifier" position="bottom"
                        slot="functions"
                        ?visible=${this.showSettings}
                        sticky
                        label=${LoggedInUser.fullName}>
                <sh-icon icon="cancel" slot="functions" size="s" button @click="${this.hideSettingsPopover}"></sh-icon>
                <popover-label-additional-line>
                    ${username}
                </popover-label-additional-line>
                <sh-divider></sh-divider>
                <pseudo-menu-item>
                    <cor-theme-selector></cor-theme-selector>
                </pseudo-menu-item>
                </div>
                <sh-menu-item label="Change password" @click=${this.doChangePasswordModal}></sh-menu-item>
                <sh-menu-item label="Log off" @click=${this.logOut}></sh-menu-item>
            </sh-popover>` : html ``;
        let userDependentElements = LoggedInUser.userDetails !== undefined ? // Should always be true.
            html `
                <sh-user-identifier id="userIdentifier"
                                    name="${username}"
                                    info="${LoggedInUser.fullName}"
                                    @click=${this.showSettingsPopover}
                                    slot="functions">
                </sh-user-identifier>
                ${settingsPopover}
                ` :
            html ``;
        return html `
            <sh-access-bar id="access" label="Corindus GRX-R Systems Dashboard" logo="./assets/img/Corindus Logo.svg" slot="access">
                ${userDependentElements}
            </sh-access-bar>     
            <sh-modal label='Change Your Password'
                     modal-height="400px"
                     modal-width="650px"
                     ?visible=${this.showChangePasswordModal}>
                <div id="changePasswordContents">
                    <div>
                       <sh-text size="body-1">Please make sure to use 8 or more characters with at least one letter, 
                           one number, and one special character. The new password must not include your username 
                           and must not be the same as the old password.</sh-text>
                    </div>
    
                    <sh-input-text id="oldPassword" label='Old Password' value="${this.oldPassword}"
                            mandatory
                            @input=${this.inputHelper('oldPassword')}
                            type="password">
                    </sh-input-text>
                    <sh-input-text id="newPassword" label='New Password' value="${this.newPassword}"
                            mandatory
                            @input=${this.inputHelper('newPassword')}
                            ?error=${this.validationTracker.error('newPassword', this.isValidNewPassword.bind(this))}
                            error-message="Password does not meet complexity requirements."
                            type="password">
                    </sh-input-text>
                    <sh-input-text id="confirmPassword" label='Confirm New Password'
                            value="${this.confirmPassword}"
                            mandatory
                            @input=${this.inputHelper('confirmPassword')}
                            ?error=${this.validationTracker.error('confirmPassword', this.isValidConfirmPassword.bind(this))}
                            error-message="Confirmation password must match the new password."
                            type="password">
                    </sh-input-text>
                </div>
                <sh-button slot="footer" color="secondary" label="Cancel" 
                            @click=${this.cancelChangePassword}></sh-button>
                <sh-button slot="footer" color="primary" label="Change Password" size="l"
                           ?disabled=${this.validationTracker.anyInvalid ||
            !this.oldPassword ||
            !this.newPassword ||
            !this.confirmPassword}
                            @click=${this.doChangePassword}></sh-button>
           </sh-modal>

            <sh-popover id="successOrFailure" label="${this.popoverTitle}" passive position="top-right" target="access"
                        ?sticky=${this.popoverIsError}
                        type="${this.popoverIsError ? "error" : "confirmation"}"
                        ?visible=${this.showPopover}>
                <sh-icon icon="cancel" size="s" button slot="functions" @click="${this.hidePopover}"></sh-icon>
                <sh-text>${this.popoverText}</sh-text>
            </sh-popover>

            <sh-popover id="passwordExpiresSoon" label="Password Expires Soon" passive position="top-right" target="access"
                        ?sticky=${true}
                        type="alert"
                        ?visible=${this.showPasswordExpiresSoon}>
                <sh-icon icon="cancel" size="s" button slot="functions" @click="${this.cancelPasswordExpiresSoon}"></sh-icon>
                <sh-text>Your password will expire soon. You may change your password in Account Settings
                        or by pressing the button below.</sh-text>
                <sh-button slot="footer" color="secondary" size="l" label="Change Password"
                        @click=${this.doChangePasswordModal}></sh-button>
            </sh-popover>
        `;
    }
};
__decorate([
    internalProperty()
], CorAccessBar.prototype, "showSettings", void 0);
__decorate([
    internalProperty()
], CorAccessBar.prototype, "showChangePasswordModal", void 0);
__decorate([
    internalProperty()
], CorAccessBar.prototype, "oldPassword", void 0);
__decorate([
    internalProperty()
], CorAccessBar.prototype, "newPassword", void 0);
__decorate([
    internalProperty()
], CorAccessBar.prototype, "confirmPassword", void 0);
__decorate([
    internalProperty()
], CorAccessBar.prototype, "popoverTitle", void 0);
__decorate([
    internalProperty()
], CorAccessBar.prototype, "popoverIsError", void 0);
__decorate([
    internalProperty()
], CorAccessBar.prototype, "popoverText", void 0);
__decorate([
    internalProperty()
], CorAccessBar.prototype, "showPopover", void 0);
__decorate([
    internalProperty()
], CorAccessBar.prototype, "showPasswordExpiresSoon", void 0);
CorAccessBar = __decorate([
    customElement('cor-access-bar')
], CorAccessBar);
export { CorAccessBar };
//# sourceMappingURL=CorAccessBar.js.map